// Audio export helpers: WAV encoding, MP3 encoding (via lamejs), master bus DSP.
//
// applyMasterBus design — iteration informed by Gemini-2.5 code review (2026-05-27):
//   1. K-weighted LUFS estimation (ITU-R BS.1770 inspired) — high-shelf @1.5kHz +4dB
//      plus high-pass @100Hz fed into RMS measurement, so loudness target matches
//      perceived loudness instead of raw RMS.
//   2. Multi-band compression — Linkwitz-Riley 4th-order crossover at 200Hz and
//      3.5kHz (cascaded 2nd-order biquads). Mid band gets the bus compressor;
//      lows and highs pass clean. Avoids the kick-pumps-vocals problem of wideband bus comp.
//   3. Soft-clip waveshaper + faster (0.1ms) brickwall limiter — even-order harmonics
//      for warmth, true brickwall to catch fast plosives.

import lamejs from "lamejs";

// Encode AudioBuffer to a 16-bit PCM WAV Blob
export function audioBufferToWavBlob(buffer) {
  const numCh = Math.max(1, buffer.numberOfChannels);
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  const bytesPerSample = 2;
  const dataSize = length * numCh * bytesPerSample;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numCh * bytesPerSample, true);
  view.setUint16(32, numCh * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  let off = 44;
  const chData = [];
  for (let ch = 0; ch < numCh; ch++) chData.push(buffer.getChannelData(ch));
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      let s = Math.max(-1, Math.min(1, chData[ch][i]));
      s = s < 0 ? s * 0x8000 : s * 0x7FFF;
      view.setInt16(off, s, true);
      off += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

export async function audioBufferToMp3Blob(buffer, bitrateKbps = 320) {
  const numCh = Math.min(2, Math.max(1, buffer.numberOfChannels));
  const sampleRate = buffer.sampleRate;
  const encoder = new lamejs.Mp3Encoder(numCh, sampleRate, bitrateKbps);
  const toInt16 = (f32) => {
    const i16 = new Int16Array(f32.length);
    for (let i = 0; i < f32.length; i++) {
      const s = Math.max(-1, Math.min(1, f32[i]));
      i16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return i16;
  };
  const left = toInt16(buffer.getChannelData(0));
  const right = numCh > 1 ? toInt16(buffer.getChannelData(1)) : left;
  const FRAME = 1152;
  const parts = [];
  for (let i = 0; i < left.length; i += FRAME) {
    const mp3 = encoder.encodeBuffer(left.subarray(i, i + FRAME), right.subarray(i, i + FRAME));
    if (mp3.length > 0) parts.push(mp3);
  }
  const last = encoder.flush();
  if (last.length > 0) parts.push(last);
  return new Blob(parts, { type: "audio/mp3" });
}

// K-weighted LUFS estimation: render buffer through K-weighting filters offline,
// compute RMS of weighted signal, return perceptual loudness estimate.
async function estimateKWeightedLUFS(sourceBuffer) {
  const numCh = sourceBuffer.numberOfChannels;
  const ctx = new OfflineAudioContext(numCh, sourceBuffer.length, sourceBuffer.sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = sourceBuffer;
  // K-weighting (simplified BS.1770 pre-filters)
  const hpf = ctx.createBiquadFilter();
  hpf.type = "highpass";
  hpf.frequency.value = 100;
  hpf.Q.value = 0.707;
  const shelf = ctx.createBiquadFilter();
  shelf.type = "highshelf";
  shelf.frequency.value = 1500;
  shelf.gain.value = 4;
  src.connect(hpf);
  hpf.connect(shelf);
  shelf.connect(ctx.destination);
  src.start();
  const weighted = await ctx.startRendering();
  let sumSq = 0;
  const wd = weighted.getChannelData(0);
  for (let i = 0; i < wd.length; i++) sumSq += wd[i] * wd[i];
  const rms = Math.sqrt(sumSq / wd.length);
  return 20 * Math.log10(Math.max(rms, 1e-6)) - 0.691;
}

// Master bus chain — multi-band, K-weighted, soft-clipped, brickwall-limited.
export async function applyMasterBus(sourceBuffer, options = {}) {
  const {
    targetLUFS = -14,
    ceilingDb = -1.0,
    crossoverLowHz = 200,
    crossoverHighHz = 3500,
    midCompThresholdDb = -20,
    midCompRatio = 3.0,
    softClipAmount = 0.85, // 0..1, lower = more saturation
  } = options;
  const sampleRate = sourceBuffer.sampleRate;
  const numCh = sourceBuffer.numberOfChannels;

  // K-weighted LUFS estimate
  const estLUFS = await estimateKWeightedLUFS(sourceBuffer);
  const gainDb = targetLUFS - estLUFS;
  const gainLin = Math.pow(10, gainDb / 20);

  const offline = new OfflineAudioContext(numCh, sourceBuffer.length, sampleRate);
  const src = offline.createBufferSource();
  src.buffer = sourceBuffer;

  // Helper: cascade two biquads of the same type for 4th-order Linkwitz-Riley response
  const makeLP4 = (freq) => {
    const a = offline.createBiquadFilter();
    const b = offline.createBiquadFilter();
    a.type = b.type = "lowpass";
    a.frequency.value = b.frequency.value = freq;
    a.Q.value = b.Q.value = 0.707;
    a.connect(b);
    return { input: a, output: b };
  };
  const makeHP4 = (freq) => {
    const a = offline.createBiquadFilter();
    const b = offline.createBiquadFilter();
    a.type = b.type = "highpass";
    a.frequency.value = b.frequency.value = freq;
    a.Q.value = b.Q.value = 0.707;
    a.connect(b);
    return { input: a, output: b };
  };

  // 3-band split
  const lowBand = makeLP4(crossoverLowHz);                          // <200Hz
  const midBandHP = makeHP4(crossoverLowHz);                        // >200Hz
  const midBandLP = makeLP4(crossoverHighHz);                       // <3.5kHz on top of midBandHP
  midBandHP.output.connect(midBandLP.input);
  const highBand = makeHP4(crossoverHighHz);                        // >3.5kHz

  src.connect(lowBand.input);
  src.connect(midBandHP.input);
  src.connect(highBand.input);

  // Mid-band compressor: 3:1 ratio, slow attack to preserve transients, soft knee
  const midComp = offline.createDynamicsCompressor();
  midComp.threshold.value = midCompThresholdDb;
  midComp.knee.value = 12;
  midComp.ratio.value = midCompRatio;
  midComp.attack.value = 0.015;
  midComp.release.value = 0.200;
  midBandLP.output.connect(midComp);

  // Recombine bands into a mix bus
  const mixBus = offline.createGain();
  mixBus.gain.value = 1.0;
  lowBand.output.connect(mixBus);
  midComp.connect(mixBus);
  highBand.output.connect(mixBus);

  // Master gain (LUFS targeting)
  const masterGain = offline.createGain();
  masterGain.gain.value = gainLin;
  mixBus.connect(masterGain);

  // Soft-clip waveshaper for harmonic warmth and transient shaving
  const shaper = offline.createWaveShaper();
  const curveLen = 4096;
  const curve = new Float32Array(curveLen);
  for (let i = 0; i < curveLen; i++) {
    const x = (i / (curveLen - 1)) * 2 - 1;
    // x - (1/3)x^3 soft-clip with adjustable softness
    const k = softClipAmount;
    curve[i] = (1 - k) * x + k * (x - (x * x * x) / 3);
  }
  shaper.curve = curve;
  shaper.oversample = "2x";
  masterGain.connect(shaper);

  // Brickwall limiter at ceiling — 0.1ms attack catches fast plosives
  const limiter = offline.createDynamicsCompressor();
  limiter.threshold.value = ceilingDb;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.0001;
  limiter.release.value = 0.05;
  shaper.connect(limiter);
  limiter.connect(offline.destination);

  src.start();
  const rendered = await offline.startRendering();

  // Final metrics — also K-weighted for an apples-to-apples final LUFS
  const finalLUFS = await estimateKWeightedLUFS(rendered);
  let finalPeak = 0;
  const fch = rendered.getChannelData(0);
  for (let i = 0; i < fch.length; i++) {
    const a = Math.abs(fch[i]);
    if (a > finalPeak) finalPeak = a;
  }

  return {
    buffer: rendered,
    metrics: {
      sourceLUFS: parseFloat(estLUFS.toFixed(1)),
      targetLUFS,
      finalLUFS: parseFloat(finalLUFS.toFixed(1)),
      finalPeakDb: parseFloat((20 * Math.log10(Math.max(finalPeak, 1e-6))).toFixed(1)),
      gainAppliedDb: parseFloat(gainDb.toFixed(1)),
      ceilingDb,
      chain: [
        "K-weighted LUFS estimate",
        "Linkwitz-Riley 4th-order crossover @200Hz",
        "Linkwitz-Riley 4th-order crossover @3.5kHz",
        "Mid-band compressor (3:1, 15ms attack)",
        "Master gain (LUFS-driven)",
        "Soft-clip waveshaper (cubic, 2x oversample)",
        "Brickwall limiter (0.1ms attack, 20:1)",
      ],
    },
  };
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
